/**
 * The maths behind the chart of the state.
 *
 * Everything here is pure: given the mapped entities, who holds their posts
 * and what is waiting for review, it works out where each entity sits, what
 * colour it carries and what text fits on it. The component draws the result
 * and owns nothing but the view.
 */

import type { GovNodeKind, GovNodeView, GovOfficeHolder, GovUpdateView } from './types';

import { foldText } from '../../utils/fuzzySearch';
export type ChartMode = 'tree' | 'radial';
export type ColourBy = 'branch' | 'kind' | 'status' | 'people';

/** The country itself, which no mapped entity can share an id with */
export const ROOT_ID = '__country__';

/* ------------------------------------------------------------------ */
/* Sizes                                                               */
/* ------------------------------------------------------------------ */

export const CARD_W = 240;
export const CARD_H = 68;
/** Between one card and the next down the page */
export const ROW_GAP = 16;
/** Between one level and the next across the page */
export const COL_GAP = 68;
/** Between one ring of the radial chart and the next */
export const RING = 176;
/** Blank slots left between two branches of the state, so groups read apart */
export const BRANCH_GAP = 1;
/** How far below its first child a body may be drawn, in slots */
export const PARENT_DROP = 2;

export const ROW_STEP = CARD_H + ROW_GAP;
export const COL_STEP = CARD_W + COL_GAP;

/* ------------------------------------------------------------------ */
/* People                                                              */
/* ------------------------------------------------------------------ */

/** The short name of a post, so a card says "Minister", not the whole title */
export const POST_LABEL: Record<string, string> = {
  president: 'President',
  vice_president: 'Vice-president',
  prime_minister: 'Prime Minister',
  minister: 'Minister',
  deputy_minister: 'Deputy minister',
  secretary_general: 'Secretary General',
  deputy_secretary_general: 'Deputy Secretary General',
  chief_of_staff: 'Chief of staff',
  head_of_office: 'Head of office',
  military_chief: 'Chief of staff (military)',
  director_general: 'Director General',
  deputy_director_general: 'Deputy Director General',
  governor: 'Governor',
  deputy_governor: 'Deputy governor',
  ambassador: 'Ambassador',
  board_chair: 'Board chair',
  inspector_general: 'Inspector General',
  spokesperson: 'Spokesperson',
  mayor: 'Mayor',
  advisor: 'Adviser',
  assistant: 'Assistant',
  director: 'Director',
  member: 'Member',
};

export interface ChartPerson {
  /** The directory person, when there is one */
  stakeholderId: string | null;
  name: string;
  /** "Minister", or the role as it is written when it is not a post we name */
  role: string | null;
  /** The canonical post, when the directory holds the role */
  post: string | null;
  imageId: string | null;
  /**
   * True when the directory records this role. False when only research knows
   * the name — which is exactly the gap the chart is there to show.
   */
  recorded: boolean;
}

const personOf = (holder: GovOfficeHolder): ChartPerson => ({
  stakeholderId: holder.stakeholderId,
  name: holder.name,
  role: POST_LABEL[holder.post] ?? holder.role ?? null,
  post: holder.post,
  imageId: holder.imageId,
  recorded: true,
});

/**
 * Posts only one person can hold at a time.
 *
 * Mirrors `SINGLE_HOLDER_POSTS` in the backend's `utils/posts.ts`: two people
 * recorded as the minister of one ministry is not a fact about the state, it
 * is a fault in the directory, and the chart says so.
 */
export const SINGLE_HOLDER = new Set([
  'president',
  'prime_minister',
  'minister',
  'secretary_general',
  'chief_of_staff',
  'head_of_office',
  'military_chief',
  'director_general',
  'governor',
  'ambassador',
  'board_chair',
  'inspector_general',
  'spokesperson',
  'mayor',
]);

/** The post two or more people are recorded in here, if there is one */
export function contestedPost(holders: ChartPerson[]): string | null {
  const held = new Map<string, number>();
  for (const person of holders) {
    if (!person.post || !SINGLE_HOLDER.has(person.post)) continue;
    held.set(person.post, (held.get(person.post) ?? 0) + 1);
  }
  let found: string | null = null;
  held.forEach((count, post) => {
    if (count > 1 && !found) found = POST_LABEL[post] ?? post;
  });
  return found;
}

/** The post that leads this kind of body */
const HEAD_POSTS: Record<string, string[]> = {
  head_of_state: ['president'],
  head_of_government: ['prime_minister'],
  ministry: ['minister'],
  legislature: ['president', 'board_chair'],
  judiciary: ['president', 'board_chair', 'director_general'],
  independent_authority: ['president', 'board_chair', 'director_general'],
  local_government: ['governor', 'mayor'],
  diplomatic_mission: ['ambassador'],
  security_force: ['director_general', 'inspector_general', 'chief_of_staff'],
  state_enterprise: ['director_general', 'board_chair'],
  agency: ['director_general', 'director', 'president'],
  directorate: ['director_general', 'director'],
  other: ['president', 'director_general', 'director'],
};

const sameName = (a: string, b: string): boolean => {
  const tidy = (value: string) =>
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z ]/g, ' ')
      .split(/\s+/)
      .filter((word) => word.length > 2)
      .sort()
      .join(' ');
  return !!a && !!b && tidy(a) === tidy(b);
};

/**
 * Whose name a body carries on the chart.
 *
 * The question a chart of the state answers first is who runs each body, and
 * the answer is not simply the first person the directory happens to hold:
 * a communications director is not the President. So the directory's own head
 * of that kind of body wins, and when the directory has no head but research
 * found one, the chart says so rather than putting somebody else's face on it
 * — that gap is worth seeing.
 */
export function pickHead(
  holders: ChartPerson[],
  research: ChartPerson | null,
  kind: GovNodeKind | 'country',
): ChartPerson | null {
  if (research) {
    const agreed = holders.find((person) => sameName(person.name, research.name));
    if (agreed) return agreed;
  }
  const heads = HEAD_POSTS[kind] ?? HEAD_POSTS.other;
  const held = holders.find((person) => heads.includes(person.post ?? ''));
  return held ?? research ?? holders[0] ?? null;
}

/**
 * Who heads one body, as its card on the chart would name them — the same
 * rule wherever the question is asked, so the tree, the panel and the chart
 * never disagree about who runs a ministry.
 */
export function headOf(node: GovNodeView, holders: GovOfficeHolder[]): ChartPerson | null {
  const research: ChartPerson | null = node.head?.name
    ? {
        stakeholderId: node.head.stakeholderId ?? null,
        name: node.head.name,
        role: node.head.title ?? null,
        post: null,
        imageId: null,
        recorded: false,
      }
    : null;
  return pickHead(holders.map(personOf), research, node.kind);
}

/** Whether the directory's person and research's person are the same one */
export const samePerson = (a: string, b: string): boolean => sameName(a, b);

/* ------------------------------------------------------------------ */
/* The tree                                                            */
/* ------------------------------------------------------------------ */


export interface ChartItem {
  id: string;
  /** The mapped entity; null for the country itself, which sits at the root */
  node: GovNodeView | null;
  label: string;
  /** Acronym, or a shortened name, for when there is no room for the label */
  short: string;
  kind: GovNodeKind | 'country';
  depth: number;
  /** The top-level body this belongs to — presidency, government, a court */
  branch: string;
  parentId: string | null;
  children: ChartItem[];
  /** Children folded away, and how many entities are under there in total */
  collapsed: boolean;
  hidden: number;
  descendants: number;
  /** Everyone the directory records here, most senior first */
  holders: ChartPerson[];
  /** The one a card has room for */
  person: ChartPerson | null;
  /** A post two or more people are recorded in, which cannot be right */
  contested: string | null;
  /**
   * Where the map would put this body, when the directory has filed it
   * somewhere else — "under the Presidency", "at the top level". Null when
   * the two agree, which is nearly always.
   */
  mapPlacement: string | null;
  people: number;
  pending: number;
  inDirectory: boolean;
  /** Position along the sibling axis, in slots */
  cross: number;
  /** The middle of its children, which is where the ring puts it */
  mean: number;
  span: [number, number];
  /** Left-to-right tree, top-left corner of the card */
  x: number;
  y: number;
  /** Radial, from the centre */
  angle: number;
  radius: number;
}

export interface ChartLink {
  id: string;
  source: ChartItem;
  target: ChartItem;
}

export interface Chart {
  root: ChartItem;
  items: ChartItem[];
  links: ChartLink[];
  byId: Map<string, ChartItem>;
  slots: number;
  maxDepth: number;
  /** The whole left-to-right tree, card corners included */
  bounds: { minX: number; minY: number; maxX: number; maxY: number };
  /** Half the width of the radial chart, labels not counted */
  radius: number;
}

export interface ChartOptions {
  countryName: string;
  collapsed?: Set<string>;
  holdersByOrg?: Map<string, GovOfficeHolder[]>;
  pendingByOrg?: Map<string, number>;
  showRejected?: boolean;
}

/** An acronym, or the first words of a name — for when a label will not fit */
const JOINING = /^(de|du|des|d['’]|la|le|les|of|the|and|et|à|au|aux|pour|en)$/i;

export function shortLabel(name: string, acronym?: string | null): string {
  if (acronym && acronym.trim()) return acronym.trim();
  const stripped = name.replace(
    /^(ministère|ministry|minist[eé]rio|department|direction)\s+(de\s+la|de\s+l['’]|des|du|de|of|the)?\s*/i,
    '',
  );
  const words = (stripped || name).split(/\s+/).filter(Boolean).slice(0, 3);
  // "Présidence de la" is not a name — stop before the words that join
  while (words.length > 1 && JOINING.test(words[words.length - 1])) words.pop();
  return words.join(' ');
}

/**
 * Lays the mapped state out as a tree.
 *
 * Every entity with nothing under it takes one slot along the sibling axis,
 * and every entity above sits at the middle of the slots its children take.
 * Two branches therefore never overlap, whatever shape the state turns out
 * to have, and the same numbers serve the left-to-right chart and the ring.
 */
export function buildChart(nodes: GovNodeView[], options: ChartOptions): Chart {
  const visible = nodes.filter(
    (node) => options.showRejected || node.status !== 'rejected',
  );
  const childrenOf = childrenByParent(visible);
  const collapsed = options.collapsed ?? new Set<string>();
  const holdersByOrg = options.holdersByOrg ?? new Map<string, GovOfficeHolder[]>();
  const pendingByOrg = options.pendingByOrg ?? new Map<string, number>();

  const items: ChartItem[] = [];
  const links: ChartLink[] = [];
  const names = new Map(visible.map((node) => [node.id, node.name]));
  let slot = 0;

  const make = (
    node: GovNodeView | null,
    depth: number,
    branch: string,
    parentId: string | null,
  ): ChartItem => {
    const id = node ? node.id : ROOT_ID;
    const holders = node?.organizationId
      ? (holdersByOrg.get(node.organizationId) ?? []).map(personOf)
      : [];
    const research: ChartPerson | null = node?.head?.name
      ? {
          stakeholderId: node.head.stakeholderId ?? null,
          name: node.head.name,
          role: node.head.title ?? null,
          post: null,
          imageId: null,
          recorded: false,
        }
      : null;
    const item: ChartItem = {
      id,
      node,
      label: node ? node.name : options.countryName,
      short: node ? shortLabel(node.name, node.acronym) : options.countryName,
      kind: node ? node.kind : 'country',
      depth,
      branch,
      // The parent it is actually drawn under: an entity whose parent was
      // rejected hangs off the country rather than vanishing with it
      parentId,
      children: [],
      collapsed: false,
      hidden: 0,
      descendants: 0,
      holders,
      person: pickHead(holders, research, node ? node.kind : 'country'),
      contested: contestedPost(holders),
      mapPlacement: mapPlacementOf(node, parentId, names),
      people: node?.organization?.people ?? 0,
      pending: node?.organizationId ? (pendingByOrg.get(node.organizationId) ?? 0) : 0,
      inDirectory: node ? node.status === 'applied' : true,
      cross: 0,
      mean: 0,
      span: [0, 0],
      x: 0,
      y: 0,
      angle: 0,
      radius: 0,
    };

    const kids = childrenOf.get(node ? node.id : null) ?? [];
    const fold = !!node && collapsed.has(node.id) && kids.length > 0;
    item.collapsed = fold;

    if (!kids.length || fold) {
      item.cross = slot;
      item.mean = slot;
      item.span = [slot, slot];
      slot += 1;
      // A folded branch still says how much is under it
      if (fold) item.hidden = countBelow(kids, childrenOf);
      item.descendants = fold ? item.hidden : 0;
    } else {
      const start = slot;
      item.children = kids.map((child, index) => {
        const built = make(child, depth + 1, depth === 0 ? child.id : branch, id);
        // Branches of the state are held apart so the groups read as groups
        if (depth === 0 && index < kids.length - 1) slot += BRANCH_GAP;
        return built;
      });
      const first = item.children[0];
      const last = item.children[item.children.length - 1];
      item.mean = (first.mean + last.mean) / 2;
      // Centred over its children, but never more than a couple of rows below
      // the first of them: a ministry with forty directorates under it would
      // otherwise sit twenty rows away from the first thing it governs
      item.cross = Math.min(
        (first.cross + last.cross) / 2,
        first.cross + PARENT_DROP,
      );
      item.span = [start, Math.max(start, slot - 1)];
      item.descendants = item.children.reduce(
        (sum, child) => sum + 1 + child.descendants,
        0,
      );
      for (const child of item.children) {
        links.push({ id: `${item.id}->${child.id}`, source: item, target: child });
      }
    }
    items.push(item);
    return item;
  };

  const root = make(null, 0, ROOT_ID, null);
  const slots = Math.max(1, slot);
  const maxDepth = items.reduce((deep, item) => Math.max(deep, item.depth), 0);

  // The same slots, read twice: down a page, and around a circle
  const turn = (Math.PI * 2) / slots;
  for (const item of items) {
    item.x = item.depth * COL_STEP;
    item.y = item.cross * ROW_STEP;
    // The ring has room to centre a body over everything under it
    item.angle = (item.mean + 0.5) * turn - Math.PI / 2;
    item.radius = item.depth * RING;
  }

  const bounds = {
    minX: 0,
    minY: 0,
    maxX: maxDepth * COL_STEP + CARD_W,
    maxY: (slots - 1) * ROW_STEP + CARD_H,
  };

  return {
    root,
    items,
    links,
    byId: new Map(items.map((item) => [item.id, item])),
    slots,
    maxDepth,
    bounds,
    radius: maxDepth * RING,
  };
}

function countBelow(
  kids: GovNodeView[],
  childrenOf: Map<string | null, GovNodeView[]>,
  depth = 0,
): number {
  if (depth > 20) return kids.length;
  return kids.reduce(
    (sum, kid) => sum + 1 + countBelow(childrenOf.get(kid.id) ?? [], childrenOf, depth + 1),
    0,
  );
}

/**
 * Where each body hangs, as the directory has it.
 *
 * The blueprint says where research believes a body sits; the directory says
 * where it actually sits, and the directory is what the graph draws. For a
 * body with a record, the parent is whatever that record is filed under —
 * nothing, when it is filed at the top. The blueprint's placement is used
 * only where the directory cannot answer: a body with no record yet, or one
 * filed under something that is not part of the state.
 *
 * Both sides guard against loops on their own, but a tree made of one and
 * then the other could still close on itself, so anything that comes back
 * round to where it started is cut loose and drawn at the top.
 */
export function drawnParents(nodes: GovNodeView[]): Map<string, string | null> {
  const nodeOfOrganization = new Map<string, string>();
  for (const node of nodes) {
    if (node.organizationId) nodeOfOrganization.set(node.organizationId, node.id);
  }
  const known = new Set(nodes.map((node) => node.id));

  const parents = new Map<string, string | null>();
  for (const node of nodes) {
    const record = node.organization;
    let parent: string | null;
    if (record) {
      parent = record.parentId ? (nodeOfOrganization.get(record.parentId) ?? null) : null;
      // Filed under something outside the state: keep the blueprint's word
      // rather than pretending it stands at the top of the government
      if (!parent && record.parentId) parent = node.parentId ?? null;
    } else {
      parent = node.parentId ?? null;
    }
    parents.set(node.id, parent && known.has(parent) && parent !== node.id ? parent : null);
  }

  for (const node of nodes) {
    const seen = new Set<string>([node.id]);
    let current = parents.get(node.id) ?? null;
    for (let depth = 0; current && depth < 40; depth++) {
      if (seen.has(current)) {
        parents.set(node.id, null);
        break;
      }
      seen.add(current);
      current = parents.get(current) ?? null;
    }
  }
  return parents;
}

/**
 * What the map says about a body's placement, when the directory disagrees.
 *
 * The graph follows the directory, so where the two differ the map's reading
 * would otherwise vanish. It is worth a line: one of them is out of date, and
 * only a person can say which.
 */
export function mapPlacementOf(
  node: GovNodeView | null,
  drawnParentId: string | null,
  names: Map<string, string>,
): string | null {
  if (!node) return null;
  const drawn = drawnParentId === ROOT_ID ? null : drawnParentId;
  const mapped = node.parentId && names.has(node.parentId) ? node.parentId : null;
  if (mapped === drawn) return null;
  return mapped ? `under ${names.get(mapped)}` : 'at the top level';
}

/** Children by drawn parent, in the order research found them */
function childrenByParent(
  nodes: GovNodeView[],
): Map<string | null, GovNodeView[]> {
  const parents = drawnParents(nodes);
  const childrenOf = new Map<string | null, GovNodeView[]>();
  for (const node of nodes) {
    const parent = parents.get(node.id) ?? null;
    childrenOf.set(parent, [...(childrenOf.get(parent) ?? []), node]);
  }
  childrenOf.forEach((list, key) => {
    childrenOf.set(
      key,
      [...list].sort((a, b) => a.order - b.order || a.name.localeCompare(b.name)),
    );
  });
  return childrenOf;
}

/**
 * The entities to fold away so the chart stops at a given level.
 *
 * Level 1 is the top of the state — the presidency, the government, the
 * courts. Everything at that level that has something under it is folded, so
 * the reader sees the shape of the state before its detail.
 */
export function collapseBeyond(nodes: GovNodeView[], level: number): Set<string> {
  const childrenOf = childrenByParent(nodes.filter((node) => node.status !== 'rejected'));
  const fold = new Set<string>();
  const walk = (list: GovNodeView[], depth: number) => {
    if (depth > 20) return;
    for (const node of list) {
      const kids = childrenOf.get(node.id) ?? [];
      if (!kids.length) continue;
      if (depth >= level) fold.add(node.id);
      else walk(kids, depth + 1);
    }
  };
  walk(childrenOf.get(null) ?? [], 1);
  return fold;
}

/** Every entity from this one up to the country, itself included */
export function lineageOf(item: ChartItem | null, chart: Chart): Set<string> {
  const path = new Set<string>();
  let current = item;
  for (let depth = 0; current && depth < 30; depth++) {
    path.add(current.id);
    current = current.parentId ? (chart.byId.get(current.parentId) ?? null) : null;
  }
  return path;
}

/* ------------------------------------------------------------------ */
/* Paths                                                               */
/* ------------------------------------------------------------------ */

/** A soft S between two cards, left edge to left edge of the next column */
export function treeLink(source: ChartItem, target: ChartItem): string {
  const x1 = source.x + CARD_W;
  const y1 = source.y + CARD_H / 2;
  const x2 = target.x;
  const y2 = target.y + CARD_H / 2;
  const bend = Math.max(18, (x2 - x1) / 2);
  return `M${x1},${y1}C${x1 + bend},${y1} ${x2 - bend},${y2} ${x2},${y2}`;
}

export const polar = (radius: number, angle: number): [number, number] => [
  Math.cos(angle) * radius,
  Math.sin(angle) * radius,
];

/** A curve that leaves along one radius and arrives along the next */
export function radialLink(source: ChartItem, target: ChartItem): string {
  const [x1, y1] = polar(source.radius, source.angle);
  const [x2, y2] = polar(target.radius, target.angle);
  const mid = source.radius + (target.radius - source.radius) / 2;
  const [cx1, cy1] = polar(mid, source.angle);
  const [cx2, cy2] = polar(mid, target.angle);
  return `M${x1},${y1}C${cx1},${cy1} ${cx2},${cy2} ${x2},${y2}`;
}

/** The slice of the circle a branch of the state occupies */
export interface Wedge {
  id: string;
  label: string;
  from: number;
  to: number;
  middle: number;
  /** Entities in this branch, the branch itself included */
  size: number;
}

/**
 * How much of the circle each top-level body takes.
 *
 * A branch's share is its share of the state: the government with forty
 * ministries under it takes forty times the room of a court with none, which
 * is the first true thing the ring says.
 */
export function branchWedges(chart: Chart): Wedge[] {
  const turn = (Math.PI * 2) / Math.max(1, chart.slots);
  return chart.root.children.map((child) => {
    const from = child.span[0] * turn - Math.PI / 2;
    const to = (child.span[1] + 1) * turn - Math.PI / 2;
    return {
      id: child.id,
      // Its own name: a wedge has room for it, unlike a mark on the rim
      label: child.label,
      from,
      to,
      middle: (from + to) / 2,
      size: child.descendants + 1,
    };
  });
}

/** A slice of a ring, from one radius to another */
export function wedgePath(inner: number, outer: number, from: number, to: number): string {
  const big = to - from > Math.PI ? 1 : 0;
  const [ax, ay] = polar(inner, from);
  const [bx, by] = polar(inner, to);
  const [cx, cy] = polar(outer, to);
  const [dx, dy] = polar(outer, from);
  return `M${ax},${ay}A${inner},${inner} 0 ${big} 1 ${bx},${by}L${cx},${cy}A${outer},${outer} 0 ${big} 0 ${dx},${dy}Z`;
}

/**
 * How much room a label has, in pixels of arc at scale 1.
 *
 * A body's room is the whole wedge its branch of the tree occupies, not one
 * slot: a ministry with thirty directorates under it has thirty slots' worth
 * of room for its name, and a directorate has one.
 */
export function labelRoom(item: ChartItem, chart: Chart): number {
  const turn = (Math.PI * 2) / Math.max(1, chart.slots);
  const slots = item.span[1] - item.span[0] + 1;
  return slots * turn * Math.max(item.radius, RING * 0.7);
}

/* ------------------------------------------------------------------ */
/* Colour                                                              */
/* ------------------------------------------------------------------ */

/**
 * One hue per branch of the state. The first is the app's red, which lands on
 * the presidency in most countries because research reports it first.
 */
export const BRANCH_PALETTE = [
  '#ea2a33',
  '#0ea5e9',
  '#14b8a6',
  '#8b5cf6',
  '#f59e0b',
  '#10b981',
  '#6366f1',
  '#ec4899',
  '#0891b2',
  '#84cc16',
  '#f43f5e',
  '#3b82f6',
];

export const KIND_COLOUR: Record<GovNodeKind | 'country', string> = {
  head_of_state: '#ea2a33',
  head_of_government: '#f43f5e',
  ministry: '#0ea5e9',
  directorate: '#38bdf8',
  agency: '#14b8a6',
  state_enterprise: '#f59e0b',
  legislature: '#8b5cf6',
  judiciary: '#6366f1',
  independent_authority: '#10b981',
  local_government: '#84cc16',
  security_force: '#64748b',
  diplomatic_mission: '#ec4899',
  other: '#a3a3a3',
  country: '#0a0a0a',
};

export const STATUS_COLOUR: Record<string, string> = {
  applied: '#10b981',
  confirmed: '#64748b',
  proposed: '#f59e0b',
  rejected: '#d4d4d4',
};

/** How thickly a body is staffed in the directory, in five steps */
export function peopleColour(people: number): string {
  if (people <= 0) return '#e5e7eb';
  if (people <= 2) return '#99f6e4';
  if (people <= 5) return '#2dd4bf';
  if (people <= 10) return '#0d9488';
  return '#115e59';
}

/** Branch id → colour, fixed by the order the branches are laid out in */
export function branchColours(chart: Chart): Map<string, string> {
  const colours = new Map<string, string>();
  let index = 0;
  for (const child of chart.root.children) {
    colours.set(child.id, BRANCH_PALETTE[index % BRANCH_PALETTE.length]);
    index += 1;
  }
  return colours;
}

export function colourOf(
  item: ChartItem,
  mode: ColourBy,
  branches: Map<string, string>,
): string {
  if (item.kind === 'country') return '#0a0a0a';
  switch (mode) {
    case 'kind':
      return KIND_COLOUR[item.kind] ?? KIND_COLOUR.other;
    case 'status':
      return STATUS_COLOUR[item.node?.status ?? 'proposed'] ?? STATUS_COLOUR.proposed;
    case 'people':
      return peopleColour(item.people);
    case 'branch':
    default:
      return branches.get(item.branch) ?? '#a3a3a3';
  }
}

/** #rrggbb with an alpha, for soft fills that print the same everywhere */
export function tint(hex: string, alpha: number): string {
  const value = hex.replace('#', '');
  const full = value.length === 3 ? value.split('').map((c) => c + c).join('') : value;
  const r = parseInt(full.slice(0, 2), 16);
  const g = parseInt(full.slice(2, 4), 16);
  const b = parseInt(full.slice(4, 6), 16);
  if ([r, g, b].some((channel) => Number.isNaN(channel))) return `rgba(163,163,163,${alpha})`;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/* ------------------------------------------------------------------ */
/* Text                                                                */
/* ------------------------------------------------------------------ */

/** Roughly how wide a string is at a font size, without measuring the DOM */
export const textWidth = (text: string, size: number): number => text.length * size * 0.545;

/** One line, cut with an ellipsis when it does not fit */
export function fitText(text: string, maxWidth: number, size: number): string {
  if (!text) return '';
  if (textWidth(text, size) <= maxWidth) return text;
  const room = Math.max(1, Math.floor(maxWidth / (size * 0.545)) - 1);
  return `${text.slice(0, room).trimEnd()}…`;
}

/** As many lines as are allowed, the last one cut if the rest will not fit */
export function wrapText(
  text: string,
  maxWidth: number,
  size: number,
  maxLines = 2,
): string[] {
  const words = (text ?? '').split(/\s+/).filter(Boolean);
  if (!words.length) return [];
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (textWidth(next, size) <= maxWidth || !line) {
      line = next;
      continue;
    }
    lines.push(line);
    line = word;
    if (lines.length === maxLines) break;
  }
  if (lines.length < maxLines && line) lines.push(line);
  if (lines.length === maxLines) {
    const used = lines.join(' ').split(/\s+/).length;
    const left = words.slice(used);
    if (left.length) {
      lines[maxLines - 1] = fitText(`${lines[maxLines - 1]} ${left.join(' ')}`, maxWidth, size);
    } else {
      lines[maxLines - 1] = fitText(lines[maxLines - 1], maxWidth, size);
    }
  }
  return lines;
}

/* ------------------------------------------------------------------ */
/* What the chart says about the state                                 */
/* ------------------------------------------------------------------ */

export interface ChartInsights {
  entities: number;
  ministries: number;
  levels: number;
  /** Entities whose post the directory fills */
  staffed: number;
  /** Entities where research knows the head but the directory does not */
  researchOnly: number;
  /** Entities with nobody at all */
  empty: number;
  people: number;
  pending: number;
  notInDirectory: number;
  /** Bodies where two people are recorded in a post only one can hold */
  contested: number;
  /** Bodies the directory files somewhere other than the map's placement */
  placedElsewhere: number;
}

export function chartInsights(chart: Chart): ChartInsights {
  const insight: ChartInsights = {
    entities: 0,
    ministries: 0,
    levels: chart.maxDepth,
    staffed: 0,
    researchOnly: 0,
    empty: 0,
    people: 0,
    pending: 0,
    notInDirectory: 0,
    contested: 0,
    placedElsewhere: 0,
  };
  for (const item of chart.items) {
    if (!item.node) continue;
    insight.entities += 1;
    if (item.kind === 'ministry') insight.ministries += 1;
    insight.people += item.people;
    insight.pending += item.pending;
    if (!item.inDirectory) insight.notInDirectory += 1;
    if (item.contested) insight.contested += 1;
    if (item.mapPlacement) insight.placedElsewhere += 1;
    // Counted the way the chart draws it: whose name is on the body, and
    // whether the directory is where that name comes from
    if (item.person?.recorded) insight.staffed += 1;
    else if (item.person) insight.researchOnly += 1;
    else insight.empty += 1;
  }
  return insight;
}

/** Pending changes per entity, for the mark that says "something is waiting" */
export function pendingByOrganization(updates: GovUpdateView[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const update of updates) {
    if (update.status !== 'pending') continue;
    const id = update.organization.id;
    if (!id) continue;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  return counts;
}

export function holdersByOrganization(
  holders: GovOfficeHolder[],
): Map<string, GovOfficeHolder[]> {
  const byOrg = new Map<string, GovOfficeHolder[]>();
  for (const holder of holders) {
    byOrg.set(holder.organizationId, [...(byOrg.get(holder.organizationId) ?? []), holder]);
  }
  byOrg.forEach((list, id) => byOrg.set(id, [...list].sort((a, b) => a.rank - b.rank)));
  return byOrg;
}

/* ------------------------------------------------------------------ */
/* Searching and framing                                               */
/* ------------------------------------------------------------------ */

/** Ids whose entity answers to what was typed — name, acronym or a person */
export function searchHits(chart: Chart, term: string): Set<string> {
  const needle = foldText(term);
  if (!needle) return new Set();
  const hits = new Set<string>();
  for (const item of chart.items) {
    if (!item.node) continue;
    const hay = [
      item.label,
      item.node.nameEn,
      item.node.acronym,
      ...item.holders.map((person) => person.name),
      item.person?.name,
    ];
    if (hay.some((value) => foldText(value).includes(needle))) hits.add(item.id);
  }
  return hits;
}

export interface Viewport {
  width: number;
  height: number;
}

export interface Transform {
  k: number;
  x: number;
  y: number;
}

/**
 * The transform that frames a box in the viewport.
 *
 * A state with a hundred bodies in it does not fit on a screen at a size
 * anyone can read, so the scale has a floor: below it the chart is shown from
 * its beginning — the top of the state, on the left — and the reader moves
 * through it, rather than being handed the whole thing as a grey smear.
 */
export function frame(
  box: { minX: number; minY: number; maxX: number; maxY: number },
  view: Viewport,
  padding = 48,
  maxScale = 1.1,
  minScale = 0.08,
): Transform {
  const width = Math.max(1, box.maxX - box.minX);
  const height = Math.max(1, box.maxY - box.minY);
  const k = Math.min(
    maxScale,
    Math.max(
      minScale,
      Math.min((view.width - padding * 2) / width, (view.height - padding * 2) / height),
    ),
  );
  // Each axis on its own: a chart that is too tall is still centred across
  const place = (start: number, end: number, room: number) =>
    (end - start) * k <= room - padding * 2
      ? room / 2 - ((start + end) / 2) * k
      : padding - start * k;
  return {
    k,
    x: place(box.minX, box.maxX, view.width),
    y: place(box.minY, box.maxY, view.height),
  };
}

/** Zooms about a point on the screen, so the thing under the cursor stays put */
export function zoomAt(
  transform: Transform,
  factor: number,
  point: { x: number; y: number },
  range: [number, number] = [0.08, 2.6],
): Transform {
  const k = Math.min(range[1], Math.max(range[0], transform.k * factor));
  const scale = k / transform.k;
  return {
    k,
    x: point.x - (point.x - transform.x) * scale,
    y: point.y - (point.y - transform.y) * scale,
  };
}
